import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, ChevronLeftIcon, UserIcon, SearchIcon, PlusIcon, CheckIcon, AlertCircleIcon, XIcon } from 'lucide-react';
import { useMessages, Conversation } from './MessageContext';
import { MessageThread } from './MessageThread';
import { NewMessageModal } from './NewMessageModal';
import { formatDistanceToNow } from './utils';
interface MessageCenterProps {
  onClose: () => void;
  initialJobId?: string;
  initialContactId?: string;
}
export const MessageCenter: React.FC<MessageCenterProps> = ({
  onClose,
  initialJobId,
  initialContactId
}) => {
  const {
    conversations,
    activeConversationId,
    setActiveConversationId,
    markConversationAsRead
  } = useMessages();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conv => conv.participantName.toLowerCase().includes(searchQuery.toLowerCase()) || conv.jobName && conv.jobName.toLowerCase().includes(searchQuery.toLowerCase()));
  // Set initial conversation if initialContactId is provided
  useEffect(() => {
    if (initialContactId) {
      const conversation = conversations.find(conv => conv.participantId === initialContactId);
      if (conversation) {
        setActiveConversationId(conversation.id);
      }
    }
  }, [initialContactId, conversations, setActiveConversationId]);
  // Handle clicking on a conversation
  const handleConversationClick = (conversationId: string) => {
    setActiveConversationId(conversationId);
    markConversationAsRead(conversationId);
  };
  // Get role icon
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'foreman':
        return <UserIcon size={16} className="text-orange-500" data-id="element-298" />;
      case 'dispatch':
        return <UserIcon size={16} className="text-blue-500" data-id="element-299" />;
      case 'trucking':
        return <UserIcon size={16} className="text-purple-500" data-id="element-300" />;
      case 'driver':
        return <UserIcon size={16} className="text-green-500" data-id="element-301" />;
      case 'contractor':
        return <UserIcon size={16} className="text-red-500" data-id="element-302" />;
      case 'broker':
        return <UserIcon size={16} className="text-yellow-500" data-id="element-303" />;
      default:
        return <UserIcon size={16} className="text-gray-500" data-id="element-304" />;
    }
  };
  return <div className="fixed inset-0 bg-gray-800 bg-opacity-75 z-50 flex items-center justify-center p-4" data-id="element-305">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex overflow-hidden" data-id="element-306">
        {/* Sidebar */}
        <div className={`w-1/3 border-r ${activeConversationId && 'hidden md:block'}`} data-id="element-307">
          <div className="p-4 border-b flex items-center justify-between" data-id="element-308">
            <h2 className="font-bold text-lg flex items-center" data-id="element-309">
              <MessageSquareIcon size={20} className="mr-2" data-id="element-310" />
              Messages
            </h2>
            <div className="flex gap-2" data-id="element-311">
              <button onClick={() => setShowNewMessageModal(true)} className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200" title="New Message" data-id="element-312">
                <PlusIcon size={18} data-id="element-313" />
              </button>
              <button onClick={onClose} className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 md:hidden" title="Close" data-id="element-314">
                <XIcon size={18} data-id="element-315" />
              </button>
            </div>
          </div>
          {/* Search */}
          <div className="p-3 border-b" data-id="element-316">
            <div className="relative" data-id="element-317">
              <input type="text" placeholder="Search messages..." className="w-full pl-9 pr-4 py-2 border rounded-md" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-318" />
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-319" />
            </div>
          </div>
          {/* Conversation List */}
          <div className="overflow-y-auto h-[calc(80vh-132px)]" data-id="element-320">
            {filteredConversations.length === 0 ? <div className="p-4 text-center text-gray-500" data-id="element-321">
                <MessageSquareIcon size={32} className="mx-auto mb-2 text-gray-300" data-id="element-322" />
                <p data-id="element-323">No conversations found</p>
                <button onClick={() => setShowNewMessageModal(true)} className="mt-2 text-blue-600 text-sm font-medium" data-id="element-324">
                  Start a new conversation
                </button>
              </div> : filteredConversations.map(conversation => <div key={conversation.id} className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${activeConversationId === conversation.id ? 'bg-blue-50' : ''}`} onClick={() => handleConversationClick(conversation.id)} data-id="element-325">
                  <div className="flex items-center gap-3" data-id="element-326">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-327">
                      {getRoleIcon(conversation.participantRole)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-328">
                      <div className="flex justify-between items-center" data-id="element-329">
                        <h3 className="font-medium truncate" data-id="element-330">
                          {conversation.participantName}
                        </h3>
                        {conversation.lastMessage && <span className="text-xs text-gray-500" data-id="element-331">
                            {formatDistanceToNow(new Date(conversation.lastMessage.timestamp))}
                          </span>}
                      </div>
                      <div className="flex items-center gap-1" data-id="element-332">
                        <span className="text-xs text-gray-500 capitalize" data-id="element-333">
                          {conversation.participantRole}
                        </span>
                        {conversation.jobName && <>
                            <span className="text-xs text-gray-400" data-id="element-334">•</span>
                            <span className="text-xs text-gray-500 truncate" data-id="element-335">
                              {conversation.jobName}
                            </span>
                          </>}
                      </div>
                      {conversation.lastMessage && <p className="text-sm text-gray-600 truncate mt-1" data-id="element-336">
                          {conversation.lastMessage.isFromUser ? 'You: ' : ''}
                          {conversation.lastMessage.content}
                        </p>}
                    </div>
                    {conversation.unreadCount > 0 && <div className="bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-337">
                        {conversation.unreadCount}
                      </div>}
                  </div>
                </div>)}
          </div>
        </div>
        {/* Message Thread or Empty State */}
        {activeConversationId ? <div className="flex-1 flex flex-col" data-id="element-338">
            <div className="p-3 border-b flex items-center" data-id="element-339">
              <button onClick={() => setActiveConversationId(null)} className="p-1 mr-2 rounded-full hover:bg-gray-100 md:hidden" data-id="element-340">
                <ChevronLeftIcon size={20} data-id="element-341" />
              </button>
              <div className="flex-1" data-id="element-342">
                <h3 className="font-medium" data-id="element-343">
                  {conversations.find(c => c.id === activeConversationId)?.participantName}
                </h3>
                <div className="flex items-center gap-1" data-id="element-344">
                  <span className="text-xs text-gray-500 capitalize" data-id="element-345">
                    {conversations.find(c => c.id === activeConversationId)?.participantRole}
                  </span>
                  {conversations.find(c => c.id === activeConversationId)?.jobName && <>
                      <span className="text-xs text-gray-400" data-id="element-346">•</span>
                      <span className="text-xs text-gray-500" data-id="element-347">
                        {conversations.find(c => c.id === activeConversationId)?.jobName}
                      </span>
                    </>}
                </div>
              </div>
              <button onClick={onClose} className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 hidden md:block" title="Close" data-id="element-348">
                <XIcon size={18} data-id="element-349" />
              </button>
            </div>
            <MessageThread conversationId={activeConversationId} data-id="element-350" />
          </div> : <div className="flex-1 flex items-center justify-center p-4 bg-gray-50" data-id="element-351">
            <div className="text-center" data-id="element-352">
              <MessageSquareIcon size={48} className="mx-auto mb-3 text-gray-300" data-id="element-353" />
              <h3 className="text-lg font-medium text-gray-800 mb-1" data-id="element-354">
                Your Messages
              </h3>
              <p className="text-gray-500 mb-4" data-id="element-355">
                Select a conversation or start a new one
              </p>
              <button onClick={() => setShowNewMessageModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-356">
                New Message
              </button>
            </div>
          </div>}
      </div>
      {/* New Message Modal */}
      {showNewMessageModal && <NewMessageModal onClose={() => setShowNewMessageModal(false)} initialJobId={initialJobId} data-id="element-357" />}
    </div>;
};